import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { ArticleProvider, SiteProvider } from '@on3/ui-lib/index';
import { Adblock } from '@public/components/Analytics/Adblock';
import { Comscore } from '@public/components/Analytics/Comscore';
import { Piano } from '@public/components/Analytics/Piano';
import { Quantcast } from '@public/components/Analytics/Quantcast';
import { Sailthru } from '@public/components/Analytics/Sailthru';
import { Spiny } from '@public/components/Analytics/Spiny';
import { SubscribeHeader } from '@public/components/Header/Subscribe';
import clsx from 'clsx';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import styles from './SubscribeLayout.module.scss';

interface ISubscribeLayoutProps extends IGenericPageProps {
  title: string;
  children: React.ReactNode;
  noIndex?: boolean;
  background?: string;
  noPadding?: boolean;
  canonical?: string | null;
}

export const SubscribeLayout = ({
  children,
  title,
  siteData,
  pageType,
  background = '#f5f6f9',
  noIndex = false,
  noPadding = false,
  canonical,
  ...props
}: ISubscribeLayoutProps) => {
  const layoutStyles = useMemo(() => {
    return { backgroundColor: background };
  }, [background]);
  const router = useRouter();

  return (
    <SiteProvider
      currentSite={siteData?.currentSite}
      defaultYears={siteData?.defaultYears}
      menuData={siteData?.menuData}
      pageType={pageType || 'other'}
      secondaryMenuData={siteData?.secondaryMenuData}
      siteUrls={siteData?.siteUrls}
      sportsData={siteData?.sportsData}
      trendingMenuData={siteData?.trendingMenuData}
    >
      <ArticleProvider {...props}>
        <Head>
          {title && (
            <>
              <title key="title">{title}</title>
              <meta content={title} key="ogTitle" property="og:title" />
            </>
          )}
          <meta content="website" property="og:type" />
          {canonical && <link href={canonical} rel="canonical" />}
          {canonical && <meta content={canonical} property="og:url" />}
          {noIndex && <meta content="noindex" name="robots" />}
        </Head>
        <Quantcast key={`quantcast-${router.asPath}`} />
        <Spiny key={`spiny-${router.asPath}`} />
        <Piano key={`piano-${router.asPath}`} />
        <Sailthru key={`sailthru-${router.asPath}`} />
        <Adblock key={`adblock-${router.asPath}`} />
        <div>
          <SubscribeHeader />
          <section
            className={clsx(styles.central, {
              [styles.noPadding]: noPadding,
            })}
            style={layoutStyles}
          >
            {children}
          </section>
        </div>
      </ArticleProvider>
    </SiteProvider>
  );
};
